import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import api, { WS_URL, resolveUrl } from '../utils/api';
import { NotificationContext, ConfirmContext } from '../components/Layout';
import { getTheme } from '../utils/theme';
import VideoTrimmer from '../components/VideoTrimmer';
import UserProfileModal from '../components/UserProfileModal';

const theme = getTheme();

const myId = () => parseInt(localStorage.getItem('userId'));

function Avatar({ user, size = 38, onClick }) {
  const url = user?.author_avatar || user?.avatar || user?.sender_avatar;
  const name = user?.author_username || user?.username || '?';
  if (url) return <img src={url} alt={name} onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid rgba(${theme.colors.primaryRGB},0.25)`, cursor: onClick?'pointer':'inherit' }} onError={e => { e.target.style.display = 'none'; }} />;
  return <div onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', background: theme.colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: Math.max(11, size * 0.38), flexShrink: 0, cursor: onClick?'pointer':'inherit' }}>{name[0]?.toUpperCase()}</div>;
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return num;
}

// Heart icon for likes
const HeartFilled = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="#f87171" stroke="#f87171" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const HeartEmpty = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const CommentIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const TrashIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const ReportIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;
const ViewIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;

export default function Feed() {
  const { showNotification } = useContext(NotificationContext) || {};
  const { confirm } = useContext(ConfirmContext) || {};
  const [posts, setPosts] = useState([]);
  const [feedTab, setFeedTab] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});
  const [viewProfileId, setViewProfileId] = useState(null);
  const [reportPostId, setReportPostId] = useState(null);
  const [globalMute, setGlobalMute] = useState(true);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const wsRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchPosts = useCallback(async (reset = true) => {
    if (reset) { setLoading(true); setPosts([]); setNextPageUrl(null); }
    try {
      let params = '';
      if (feedTab === 'reels') params = '?type=reel';
      else if (feedTab === 'posts') params = '?type=post';
      else if (feedTab === 'mine') params = '?type=mine';

      let url = reset ? `/posts/${params}` : (nextPageUrl || null);
      if (!url) { if (reset) setLoading(false); return; }
      
      if (url.includes('/api/')) {
        url = url.split('/api')[1];
      }
      const res = await api.get(url);
      
      const newPosts = res.data.results || res.data;
      setPosts(prev => reset ? newPosts : [...prev, ...newPosts]);
      setNextPageUrl(res.data.next || null);
    } catch {}
    if (reset) setLoading(false);
  }, [feedTab, nextPageUrl]);

  useEffect(() => { fetchPosts(true); }, [feedTab]);

  useEffect(() => {
    const ws = new WebSocket(`${WS_URL}/ws/posts-feed/?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'new_post') {
        fetchPosts();
        showNotification?.(`${data.author_username} shared a new ${data.post_type}`);
      } else if (data.type === 'post_deleted') {
        setPosts(prev => prev.filter(p => p.id !== data.post_id));
      } else if (data.type === 'post_liked') {
        setPosts(prev => prev.map(p => p.id === data.post_id
          ? { ...p, like_count: data.like_count, view_count: data.view_count || p.view_count, is_liked: Number(data.liked_by) === Number(myId()) ? data.liked : p.is_liked } : p));
      } else if (data.type === 'new_comment') {
        setPosts(prev => prev.map(p => {
          if (p.id !== data.post_id) return p;
          const nc = { id: data.comment_id, content: data.content, author_id: data.author_id, author_username: data.author_username, author_avatar: data.author_avatar, created_at: new Date().toISOString(), replies: [], reply_count: 0, is_mine: data.author_id === myId() };
          if (data.parent_id) {
            return { ...p, comments: p.comments.map(c => c.id === parseInt(data.parent_id) ? { ...c, replies: [...(c.replies || []), nc], reply_count: (c.reply_count || 0) + 1 } : c) };
          }
          return { ...p, comments: [...(p.comments || []), nc], comment_count: (p.comment_count || 0) + 1 };
        }));
      } else if (data.type === 'stats_updated') {
        setPosts(prev => prev.map(p => p.id === data.post_id
          ? { ...p, like_count: data.like_count, view_count: data.view_count } : p));
      }
    };
    return () => ws.close();
  }, [token, fetchPosts]);

  const toggleLike = async (postId) => {
    try {
      const res = await api.post(`/posts/${postId}/like/`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_liked: res.data.liked, like_count: res.data.like_count, view_count: res.data.view_count } : p));
    } catch {}
  };

  const deletePost = async (postId) => {
    const ok = await confirm({ title: 'Delete Post?', message: 'Are you sure you want to remove this post?', icon: '🗑️', confirmText: 'Delete' });
    if (!ok) return;
    try { await api.delete(`/posts/${postId}/delete/`); setPosts(prev => prev.filter(p => p.id !== postId)); } catch {}
  };

  const deleteComment = async (commentId, postId) => {
    const ok = await confirm({ title: 'Delete Comment?', message: 'Remove this comment?', icon: '💬', confirmText: 'Delete' });
    if (!ok) return;
    try {
      await api.delete(`/comments/${commentId}/delete/`);
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        return { ...p, comments: p.comments.filter(c => c.id !== commentId), comment_count: Math.max(0, (p.comment_count || 1) - 1) };
      }));
    } catch {}
  };

  const loadAllComments = async (postId) => {
    try {
      const res = await api.get(`/posts/${postId}/comments/`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments: res.data } : p));
    } catch {}
  };

  const reportPost = async (reason) => {
    if (!reason || !reportPostId) return;
    try {
      await api.post('/report/', { report_type: 'post', reported_post_id: reportPostId, reason: reason });
      showNotification?.('Post reported to admins.');
      setReportPostId(null);
    } catch {}
  };

  const toggleComments = async (postId) => {
    const willExpand = !expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: willExpand }));
    if (willExpand) await loadAllComments(postId);
  };

  const addComment = async (postId, content, parentId = null) => {
    if (!content.trim()) return;
    try { await api.post(`/posts/${postId}/comments/`, { content, parent_id: parentId }); } catch {}
  };

  return (
    <div style={S.root}>
      {viewProfileId && <UserProfileModal userId={viewProfileId} onClose={() => setViewProfileId(null)} />}
      <div style={S.header}>
        <h2 style={S.title}>Feed</h2>
        <button style={S.createBtn} onClick={() => setShowCreate(true)}>+ Create Post</button>
      </div>

      <div style={S.tabs}>
        {[['all', 'All'], ['posts', 'Posts'], ['reels', 'Reels'], ['mine', 'My Posts']].map(([v, l]) => (
          <button key={v} style={{ ...S.tab, ...(feedTab === v ? S.activeTab : {}) }} onClick={() => setFeedTab(v)}>{l}</button>
        ))}
      </div>

      {showCreate && <CreatePostModal onClose={() => setShowCreate(false)} onCreated={fetchPosts} showNotification={showNotification} />}
      {reportPostId && <ReportModal onClose={() => setReportPostId(null)} onReport={reportPost} />}

      <div style={S.feed}>
        {loading ? (
          <div style={S.center}><div style={S.spinner} /></div>
        ) : posts.length === 0 ? (
          <div style={S.center}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{ marginBottom: 12 }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <p style={{ color: '#475569' }}>No posts yet. Be the first!</p>
          </div>
        ) : posts.map(post => (
          <PostCard key={post.id} post={post}
            expanded={expandedComments[post.id]}
            onToggleComments={() => toggleComments(post.id)}
            onLike={() => toggleLike(post.id)}
            onDelete={() => deletePost(post.id)}
            onReport={() => setReportPostId(post.id)}
            onComment={(content, parentId) => addComment(post.id, content, parentId)}
            onDeleteComment={(cId) => deleteComment(cId, post.id)}
            onAvatarClick={setViewProfileId}
            globalMute={globalMute}
            setGlobalMute={setGlobalMute}
          />
        ))}
        {nextPageUrl && (
          <button style={{...S.createBtn, width: '100%', marginTop: 16, padding: 14}} onClick={() => fetchPosts(false)}>
            Load More
          </button>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, expanded, onToggleComments, onLike, onDelete, onReport, onComment, onDeleteComment, onAvatarClick, globalMute, setGlobalMute }) {
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);
  const postRef = useRef(null);
  const viewed = useRef(false);

  const isVideo = post.media_type?.startsWith('video/') || post.post_type === 'reel';

  useEffect(() => {
    if (!postRef.current || viewed.current) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !viewed.current) {
          viewed.current = true;
          api.post(`/posts/${post.id}/view/`).catch(()=>{});
        }
      });
    }, { threshold: 0.6 });
    observer.observe(postRef.current);
    return () => observer.disconnect();
  }, [post.id]);

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    
    videoRef.current.muted = globalMute;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!videoRef.current) return;
        if (entry.isIntersecting) {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(err => console.log("Autoplay prevented:", err));
          }
        } else {
          videoRef.current.pause();
        }
      });
    }, { threshold: 0.6 });

    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [isVideo]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = globalMute;
    }
  }, [globalMute]);

  const submitComment = () => {
    const text = commentText.trim();
    if (!text) return;
    onComment(text, replyTo?.id || null);
    setCommentText('');
    setReplyTo(null);
  };


  return (
    <div ref={postRef} style={S.card}>
      <div style={S.cardHdr}>
        <Avatar user={post} size={40} onClick={() => onAvatarClick(post.author_id)} />
        <div style={{ flex: 1 }}>
          <div style={S.cardAuthor}>{post.author_username}</div>
          <div style={S.cardTime}>{timeAgo(post.created_at)} · {post.post_type === 'reel' ? 'Reel' : 'Post'}</div>
        </div>
        {!post.is_mine && <button style={S.delPostBtn} onClick={onReport} title="Report Post"><ReportIcon /></button>}
        {post.is_mine && <button style={S.delPostBtn} onClick={onDelete} title="Delete Post"><TrashIcon /></button>}
      </div>

      {post.caption && <p style={S.caption}>{post.caption}</p>}

      {post.media_url && (
        <div style={{ position: 'relative' }}>
          {isVideo ? (
            <div style={{ position: 'relative', width: '100%', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
              {videoError ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: 11, minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ⚠️ Video unavailable
                </div>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    src={resolveUrl(post.media_url)}
                    loop
                    playsInline
                    style={{ ...S.media, maxHeight: '80vh' }}
                    onClick={(e) => { e.stopPropagation(); setGlobalMute(!globalMute); }}
                    onError={() => setVideoError(true)}
                  />
                  <button
                    style={{ position: 'absolute', bottom: 12, right: 12, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', padding: 8, cursor: 'pointer', zIndex: 5 }}
                    onClick={(e) => { e.stopPropagation(); setGlobalMute(!globalMute); }}
                  >
                    {globalMute ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77zM3 9v6h4l5 5V4L7 9H3z"/>
                      </svg>
                    )}
                  </button>
                </>
              )}
            </div>
          ) : (
            <img
              src={resolveUrl(post.media_url)}
              alt="post"
              style={S.media}
              onError={e => { e.target.style.display = 'none'; }}
            />
          )}
        </div>
      )}

      <div style={S.actionsRow}>
        <button style={{ ...S.actionBtn, color: post.is_liked ? '#f87171' : '#64748b' }} onClick={onLike}>
          {post.is_liked ? <HeartFilled /> : <HeartEmpty />}
          <span>{formatNumber(post.like_count)}</span>
        </button>
        <button style={{ ...S.actionBtn, color: expanded ? theme.colors.primary : '#64748b' }} onClick={onToggleComments}>
          <CommentIcon />
          <span>{formatNumber(post.comment_count)}</span>
        </button>
        <div style={{ ...S.actionBtn, color: '#64748b', cursor: 'default' }}>
          <ViewIcon />
          <span>{formatNumber(post.view_count)}</span>
        </div>
      </div>

      {expanded && (
        <div style={S.commentsSection}>
          {(post.comments || []).map(c => (
            <CommentItem key={c.id} comment={c} onReply={(id, name) => setReplyTo({ id, username: name })} onComment={onComment} onDeleteComment={onDeleteComment} postId={post.id} onAvatarClick={onAvatarClick} />
          ))}
          <div style={S.commentInput}>
            {replyTo && (
              <div style={S.replyBadge}>
                Replying to @{replyTo.username}
                <button style={S.clearReply} onClick={() => setReplyTo(null)}>✕</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={S.commentField} value={commentText} onChange={e => setCommentText(e.target.value)}
                placeholder={replyTo ? `Reply to @${replyTo.username}...` : 'Add a comment...'}
                onKeyDown={e => e.key === 'Enter' && submitComment()} />
              <button style={S.commentSend} onClick={submitComment} disabled={!commentText.trim()}>Post</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentItem({ comment, onReply, onComment, onDeleteComment, onAvatarClick }) {
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);

  const submitReply = () => {
    if (!replyText.trim()) return;
    onComment(replyText, comment.id);
    setReplyText('');
    setShowReplyInput(false);
  };

  return (
    <div style={S.comment}>
      <Avatar user={{ author_avatar: comment.author_avatar, author_username: comment.author_username }} size={30} onClick={() => onAvatarClick(comment.author_id)} />
      <div style={{ flex: 1 }}>
        <div style={S.commentBubble}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <span style={S.commentAuthor}>{comment.author_username} </span>
              <span style={S.commentText}>{comment.content}</span>
            </div>
            {comment.is_mine && (
              <button style={S.delCommentBtn} onClick={() => onDeleteComment(comment.id)} title="Delete comment"><TrashIcon /></button>
            )}
          </div>
        </div>
        <div style={S.commentMeta}>
          <span style={{ color: '#475569', fontSize: 10 }}>{timeAgo(comment.created_at)}</span>
          <button style={S.replyBtn} onClick={() => setShowReplyInput(!showReplyInput)}>Reply</button>
          {comment.reply_count > 0 && (
            <button style={S.replyBtn} onClick={() => setShowReplies(!showReplies)}>
              {showReplies ? 'Hide replies' : `${comment.reply_count} ${comment.reply_count === 1 ? 'reply' : 'replies'}`}
            </button>
          )}
        </div>
        {showReplyInput && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input style={S.msgInput} value={replyText} 
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type a message..."
                onKeyDown={e => e.key === 'Enter' && submitReply()}
                autoFocus />
            <button style={S.commentSend} onClick={submitReply} disabled={!replyText.trim()}>Post</button>
          </div>
        )}
        {showReplies && (comment.replies || []).map(r => (
          <div key={r.id} style={{ ...S.comment, marginTop: 8, paddingLeft: 0 }}>
            <Avatar user={{ author_avatar: r.author_avatar, author_username: r.author_username }} size={24} onClick={() => onAvatarClick(r.author_id)} />
            <div style={S.commentBubble}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <span style={S.commentAuthor}>{r.author_username} </span>
                  <span style={S.commentText}>{r.content}</span>
                </div>
                {r.is_mine && (
                  <button style={S.delCommentBtn} onClick={() => onDeleteComment(r.id)} title="Delete reply"><TrashIcon /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreatePostModal({ onClose, onCreated, showNotification }) {
  const [caption, setCaption] = useState('');
  const [postType, setPostType] = useState('post');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [processedBlob, setProcessedBlob] = useState(null);
  const [processedName, setProcessedName] = useState(null);
  const [processedType, setProcessedType] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const getLimit = (f) => {
    if (f.type.startsWith('video/')) return 5 * 1024 * 1024;
    if (f.type.startsWith('image/')) return 1 * 1024 * 1024;
    return 1 * 1024 * 1024;
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    
    const limit = getLimit(f);
    setError('');
    if (f.size > limit) {
      const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
      const limitMB = limit / (1024 * 1024);
      const msg = `File is too large (${sizeMB}MB). Max limit for ${f.type.startsWith('video/') ? 'video' : 'this file'} is ${limitMB}MB. Please trim or compress it.`;
      setError(msg);
      showNotification?.(msg, 6000);
      setShowTrimmer(true);
    }

    setFile(f);
    setProcessedBlob(null);
    setProcessedName(null);
    setProcessedType(null);
    setPreview(URL.createObjectURL(f));
    if (f.type.startsWith('video/')) setPostType('reel');
  };

  const handleTrimmerDone = (blob, name, type) => {
    setProcessedBlob(blob);
    setProcessedName(name);
    setProcessedType(type);
    setPreview(URL.createObjectURL(blob));
    setShowTrimmer(false);
    
    // Re-check size after trimming
    const limit = getLimit({ type });
    if (blob.size > limit) {
      setError(`Trimmed file still exceeds ${limit / (1024 * 1024)}MB. Please trim more.`);
    } else {
      setError('');
    }
  };

  const submit = async () => {
    if (!caption.trim() && !file) { alert('Add a caption or media'); return; }
    
    const finalFile = processedBlob
      ? new File([processedBlob], processedName || file.name, { type: processedType || file.type })
      : file;

    if (finalFile) {
      const limit = getLimit(finalFile);
      if (finalFile.size > limit) {
        const msg = `File still exceeds ${limit / (1024 * 1024)}MB limit. Please trim it further.`;
        setError(msg);
        showNotification?.(msg, 6000);
        return;
      }
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('caption', caption);
      fd.append('post_type', postType);
      if (finalFile) { fd.append('media', finalFile); fd.append('media_type', finalFile.type); }
      await api.post('/posts/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showNotification?.('Post created!');
      onCreated();
      onClose();
    } catch (err) { alert(err.response?.data?.error || 'Failed to post'); }
    finally { setUploading(false); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHdr}>
          <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 15, fontWeight: 700 }}>Create Post</h3>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {['post', 'reel'].map(t => (
            <button key={t} style={{ ...S.typeBtn, ...(postType === t ? S.activeType : {}) }} onClick={() => setPostType(t)}>
              {t === 'post' ? 'Post' : 'Reel'}
            </button>
          ))}
        </div>
        
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#f87171', fontSize: 11, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>⚠️</span>
            <span style={{ flex: 1 }}>{error}</span>
          </div>
        )}

        <textarea style={S.captionInput} value={caption} onChange={e => setCaption(e.target.value)} placeholder="What's on your mind?" rows={3} maxLength={500} />
        <div style={S.mediaArea} onClick={() => fileRef.current?.click()}>
          {preview ? (
            file?.type.startsWith('video/') || processedType?.startsWith('video/')
              ? <video src={preview} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} />
              : <img src={preview} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }} alt="preview" />
          ) : (
            <div style={{ color: '#475569', textAlign: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 8 }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>Click to add photo or video</p>
              <p style={{ margin: '4px 0 0', fontSize: 10, color: '#64748b' }}>Video: 5MB | Image: 1MB</p>
            </div>
          )}
          <input ref={fileRef} type="file" style={{ display: 'none' }} accept="image/*,video/*" onChange={handleFile} />
        </div>

        {/* Trim/Compress button - only show if file is too large or specifically requested */}
        {file && file.size > getLimit(file) && (
          <button
            style={{ ...S.submitBtn, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)', marginBottom: 12 }}
            onClick={() => setShowTrimmer(true)}
          >
            {file.type.startsWith('video/') ? '✂️ Open Trimmer' : '🖼️ Compress Image'}
            {processedBlob && processedBlob.size <= getLimit(file) && ' ✓ Ready'}
          </button>
        )}

        <button style={S.submitBtn} onClick={submit} disabled={uploading || (file && file.size > getLimit(file) && !processedBlob)}>{uploading ? 'Posting...' : 'Share Post'}</button>
      </div>

      {/* VideoTrimmer overlay */}
      {showTrimmer && file && (
        <VideoTrimmer
          file={file}
          limit={getLimit(file)}
          onDone={handleTrimmerDone}
          onCancel={() => setShowTrimmer(false)}
        />
      )}
    </div>
  );
}


function ReportModal({ onClose, onReport }) {
  const [reason, setReason] = useState('');
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHdr}>
          <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 15, fontWeight: 700 }}>Report Post</h3>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 12 }}>Why are you reporting this post?</p>
        <textarea 
          style={S.captionInput} 
          value={reason} 
          onChange={e => setReason(e.target.value)} 
          placeholder="Enter reason (e.g. Inappropriate content, Spam...)" 
          rows={3} 
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button style={{ ...S.submitBtn, background: '#1e293b' }} onClick={onClose}>Cancel</button>
          <button style={S.submitBtn} onClick={() => onReport(reason)} disabled={!reason.trim()}>Submit Report</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { flex: 1, overflowY: 'auto', background: '#0a0a0f', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  title: { margin: 0, fontSize: 18, fontWeight: 800, color: '#e2e8f0' },
  createBtn: { padding: '8px 16px', borderRadius: 10, border: 'none', background: theme.colors.gradient, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  tabs: { display: 'flex', padding: '0 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tab: { flex: 1, padding: '10px 8px', border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 500, borderBottom: '2px solid transparent', transition: 'all 0.15s' },
  activeTab: { color: theme.colors.primary, borderBottomColor: theme.colors.primaryHover },
  feed: { maxWidth: 600, margin: '0 auto', padding: '16px 16px 32px', width: '100%', boxSizing: 'border-box' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' },
  spinner: { width: 32, height: 32, border: `3px solid rgba(${theme.colors.primaryRGB},0.2)`, borderTopColor: theme.colors.primaryHover, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  card: { background: '#111118', borderRadius: 16, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 16, overflow: 'hidden' },
  cardHdr: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' },
  cardAuthor: { fontWeight: 600, color: '#e2e8f0', fontSize: 12 },
  cardTime: { fontSize: 10, color: '#475569', marginTop: 2 },
  delPostBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 },
  caption: { margin: '0 16px 12px', color: '#94a3b8', fontSize: 12, lineHeight: 1.6 },
  media: { width: '100%', maxHeight: 500, objectFit: 'cover', display: 'block', background: '#0a0a0f' },
  actionsRow: { display: 'flex', gap: 4, padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.04)' },
  actionBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '6px 10px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' },
  commentsSection: { padding: '0 16px 12px', borderTop: '1px solid rgba(255,255,255,0.04)' },
  comment: { display: 'flex', gap: 10, paddingTop: 10 },
  commentBubble: { background: '#1a1a2e', borderRadius: 10, padding: '8px 12px', flex: 1 },
  commentAuthor: { fontWeight: 600, color: theme.colors.primary, fontSize: 11 },
  commentText: { color: '#94a3b8', fontSize: 11 },
  commentMeta: { display: 'flex', gap: 12, marginTop: 4, alignItems: 'center' },
  replyBtn: { background: 'none', border: 'none', color: theme.colors.primaryHover, cursor: 'pointer', fontSize: 10, padding: 0, fontWeight: 600 },
  delCommentBtn: { background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', borderRadius: 4, flexShrink: 0 },
  commentInput: { paddingTop: 10 },
  replyBadge: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: theme.colors.primary, background: `rgba(${theme.colors.primaryRGB},0.08)`, borderRadius: 6, padding: '4px 8px', marginBottom: 6 },
  clearReply: { background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: 10, padding: 0 },
  commentField: { flex: 1, padding: '8px 12px', borderRadius: 20, border: `1px solid rgba(${theme.colors.primaryRGB},0.2)`, background: '#1a1a2e', color: '#e2e8f0', fontSize: 11, outline: 'none', width: '100%', boxSizing: 'border-box' },
  commentSend: { padding: '7px 14px', borderRadius: 20, border: 'none', background: theme.colors.primaryHover, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, flexShrink: 0 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#111118', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, width: '100%', maxWidth: 480, padding: 24, maxHeight: '90vh', overflowY: 'auto' },
  modalHdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 },
  typeBtn: { flex: 1, padding: '8px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 },
  activeType: { background: '#1a1a2e', color: theme.colors.primary, borderColor: theme.colors.primaryHover },
  captionInput: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: '#1a1a2e', color: '#e2e8f0', fontSize: 12, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 14, fontFamily: 'inherit' },
  mediaArea: { border: `2px dashed rgba(${theme.colors.primaryRGB},0.3)`, borderRadius: 12, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 16, overflow: 'hidden' },
  submitBtn: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: theme.colors.gradient, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
